import { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "@config/env";
import { Modal } from "@common/Modal";

const fmt = (n) => {
  if (!n) return "0";
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000)    return (n / 1000).toFixed(1) + "K";
  return Number(n).toLocaleString();
};

const fmtDate = (d, short=true) => {
  if (!d) return "";
  const dt = new Date(d.replace(/-/g, "/"));
  return dt.toLocaleDateString("en-US", short ? { month:"short", day:"numeric" } : { month:"short", day:"numeric", year:"numeric" });
};

const CALL_LABELS = { search_call1: "Search — pass 1", search_call2: "Search — pass 2", profile_chat: "Profile chat", job_chat: "Job chat" };
const CALL_PILL   = {
  search_call1: { background: "var(--fc-primary-light)", color: "#2563EB" },
  search_call2: { background: "#111827", color: "#fff"    },
  profile_chat: { background: "var(--fc-green-light)", color: "var(--fc-green-text)" },
  job_chat:     { background: "var(--fc-amber-light)", color: "var(--fc-amber-text)" },
};
const RANGES = [{ label: "7d", value: "7" }, { label: "30d", value: "30" }, { label: "90d", value: "90" }, { label: "All", value: "all" }];

const STATS = [
  { key: "call_count",  label: "Total calls",  icon: "fa-bolt" },
  { key: "total_tokens",label: "Total tokens",  icon: "fa-coins" },
  { key: "total_input", label: "Input tokens",  icon: "fa-arrow-right-to-bracket" },
];

// Simple dependency-free bar chart - daily token usage over the selected range
function TrendChart({ trend }) {
  if (!trend || trend.length === 0) return null;

  const max = Math.max(...trend.map(t => Number(t.total_tokens) || 0), 1);
  const showEveryNth = trend.length > 20 ? Math.ceil(trend.length / 10) : 1;

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--fc-text-subtle)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
        Daily token usage
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: trend.length > 40 ? 1 : 3, height: 72, padding: "0 2px" }}>
        {trend.map((t, i) => {
          const tokens = Number(t.total_tokens) || 0;
          const heightPct = Math.max(3, Math.round((tokens / max) * 100));
          return (
            <div key={i} title={`${fmtDate(t.date, false)} — ${fmt(tokens)} tokens, ${t.call_count} call${t.call_count!==1?"s":""}`}
              style={{ flex: 1, height: "100%", display: "flex", alignItems: "flex-end", cursor: "default" }}>
              <div style={{
                width: "100%", height: `${heightPct}%`, minHeight: 2,
                background: tokens > 0 ? "var(--fc-primary)" : "var(--fc-border)",
                borderRadius: "2px 2px 0 0", opacity: tokens > 0 ? 0.85 : 0.4,
                transition: "opacity 0.15s"
              }}
              onMouseEnter={e=>e.currentTarget.style.opacity=1}
              onMouseLeave={e=>e.currentTarget.style.opacity=tokens>0?0.85:0.4} />
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
        {trend.map((t, i) => (
          (i === 0 || i === trend.length-1 || i % showEveryNth === 0) ? (
            <span key={i} style={{ fontSize: 9.5, color: "var(--fc-text-hint)", flex: showEveryNth>1?"none":1, textAlign: i===0?"left":i===trend.length-1?"right":"center" }}>
              {fmtDate(t.date)}
            </span>
          ) : null
        ))}
      </div>
    </div>
  );
}

export default function ConnectIQUsageModal({ userSlug, onClose }) {
  const [range,    setRange]    = useState("30");
  const [loading,  setLoading]  = useState(false);
  const [data,     setData]     = useState(null);

  const load = async (r) => {
    setLoading(true);
    const fd = new FormData();
    fd.append("user_slug", userSlug);
    fd.append("range", r);
    const res = await axios.post(`${API_BASE}connectiq/get_firm_usage`, fd).then(x => x.data).catch(() => null);
    if (res?.status === 1) setData(res);
    setLoading(false);
  };

  const changeRange = (r) => { setRange(r); load(r); };

  useEffect(() => { load(range); }, []);

  const title = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 34, height: 34, borderRadius: 9, background: "#111827", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <i className="fa-solid fa-robot" style={{fontSize:14, color:"#fff"}} />
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fc-text-primary)" }}>Connect IQ Usage</div>
        <div style={{ fontSize: 11, color: "var(--fc-text-subtle)" }}>
          {data?.scope === "user" ? "Your usage" : "Firm-wide usage"} · token consumption by type
        </div>
      </div>
    </div>
  );

  return (
    <Modal title={title} onClose={onClose} maxWidth={600}>
      {/* Range pills */}
      <div style={{ display: "flex", gap: 5, marginBottom: 22 }}>
        {RANGES.map(r => (
          <button key={r.value} onClick={() => changeRange(r.value)}
            style={{ padding: "6px 15px", fontSize: 12, borderRadius: 99, border: "1px solid var(--fc-border)", background: range === r.value ? "#111827" : "var(--fc-card-bg)", color: range === r.value ? "#fff" : "var(--fc-text-muted)", cursor: "pointer", fontFamily: "inherit", fontWeight: range === r.value ? 700 : 500, transition: "all 0.15s" }}>
            {r.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--fc-text-subtle)", fontSize: 13 }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading usage data...
        </div>
      ) : !data?.totals || data.totals.call_count === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 16px" }}>
          <i className="fa-solid fa-chart-line" style={{ fontSize: 24, color: "var(--fc-text-hint)", marginBottom: 10, display: "block" }} />
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--fc-text-primary)", marginBottom: 4 }}>No usage recorded</div>
          <div style={{ fontSize: 12.5, color: "var(--fc-text-subtle)" }}>Nothing to show for this time period yet.</div>
        </div>
      ) : (
        <>
          {/* Stat row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 22 }}>
            {STATS.map((s, i) => (
              <div key={i} style={{ background: "var(--fc-bg-light)", borderRadius: 12, padding: "14px 15px", border: "1px solid var(--fc-border-light)" }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                  <i className={`fa-solid ${s.icon}`} style={{ fontSize:10.5, color:"var(--fc-primary)" }} />
                  <div style={{ fontSize: 10, color: "var(--fc-text-subtle)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
                </div>
                <div style={{ fontSize: 23, fontWeight: 800, color: "var(--fc-text-primary)", lineHeight: 1 }}>{fmt(data.totals[s.key])}</div>
              </div>
            ))}
          </div>

          {/* Trend chart */}
          <TrendChart trend={data.trend} />

          <div style={{ borderTop: "1px solid var(--fc-border-light)", marginBottom: 16 }} />

          {/* Breakdown */}
          {data.breakdown?.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--fc-text-subtle)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                Usage by type
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {data.breakdown.map((row, i) => {
                  const pill = CALL_PILL[row.call_type] || { background: "var(--fc-border-light)", color: "var(--fc-text-secondary)" };
                  const pct  = data.totals.total_tokens > 0 ? Math.round((row.total_tokens / data.totals.total_tokens) * 100) : 0;
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 13px", background: "var(--fc-bg-light)", borderRadius: 10, border: "1px solid var(--fc-border-light)" }}>
                      <span style={{ fontSize: 10, padding: "3px 10px", borderRadius: 99, fontWeight: 700, flexShrink: 0, minWidth: 120, textAlign: "center", ...pill }}>
                        {CALL_LABELS[row.call_type] || row.call_type}
                      </span>
                      <div style={{ flex: 1, height: 5, background: "var(--fc-border)", borderRadius: 99, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: "#2563EB", borderRadius: 99, transition:"width 0.4s" }} />
                      </div>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--fc-text-muted)", flexShrink:0, width:28, textAlign:"right" }}>{pct}%</div>
                      <div style={{ display: "flex", gap: 16, flexShrink: 0 }}>
                        {[{ label: "Calls", value: fmt(row.call_count) }, { label: "Tokens", value: fmt(row.total_tokens) }].map((m, mi) => (
                          <div key={mi} style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 10, color: "var(--fc-text-subtle)" }}>{m.label}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--fc-text-primary)" }}>{m.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </Modal>
  );
}
